import React, { useState, useEffect } from 'react';
import { Box, Typography, Chip, IconButton, Tooltip } from '@mui/material';
import {
  CloudDone as OnlineIcon,
  CloudOff as OfflineIcon,
  Update as UpdateIcon,
  Info as InfoIcon,
  ZoomIn,
  ZoomOut,
  Bolt as SpektaIcon,
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';

const StatusBar = () => {
  const theme = useTheme();
  const [currentTime, setCurrentTime] = useState(new Date());
  const [appVersion, setAppVersion] = useState('');
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [updateStatus, setUpdateStatus] = useState('');
  const [zoomPercentage, setZoomPercentage] = useState(100);
  const isElectron = window.electronAPI !== undefined;

  // Update time every second
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  // Get app version from Electron
  useEffect(() => {
    if (isElectron && window.electronAPI.getAppVersion) {
      window.electronAPI.getAppVersion().then(version => {
        setAppVersion(version);
      });
    }
  }, [isElectron]);

  // Listen for online/offline events
  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Listen for update status from Electron
  useEffect(() => {
    if (isElectron && window.electronAPI.onUpdateStatus) {
      const unsubscribe = window.electronAPI.onUpdateStatus((status) => {
        setUpdateStatus(status);
      });

      return () => {
        if (unsubscribe) unsubscribe();
      };
    }
  }, [isElectron]);

  // Zoom functionality
  useEffect(() => {
    if (!isElectron || !window.electronAPI.getZoomLevel || !window.electronAPI.onZoomChanged) {
      return;
    }

    // Get initial zoom level
    window.electronAPI.getZoomLevel().then(({ percentage }) => {
      setZoomPercentage(percentage);
    }).catch(err => {
      console.error('Failed to get zoom level:', err);
    });

    // Listen for zoom changes
    const cleanup = window.electronAPI.onZoomChanged(({ percentage }) => {
      setZoomPercentage(percentage);
    });

    return () => {
      if (cleanup) cleanup();
    };
  }, [isElectron]);

  const handleZoomIn = () => {
    if (window.electronAPI?.zoomIn) {
      window.electronAPI.zoomIn();
    }
  };

  const handleZoomOut = () => {
    if (window.electronAPI?.zoomOut) {
      window.electronAPI.zoomOut();
    }
  };

  const handleZoomReset = () => {
    if (window.electronAPI?.zoomReset) {
      window.electronAPI.zoomReset();
    }
  };

  const formatTime = (date) => {
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    });
  };

  const formatDate = (date) => {
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  return (
    <Box
      sx={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        height: '32px',
        backgroundColor: theme.palette.mode === 'dark' ? '#1a1f2e' : '#f5f5f5',
        borderTop: theme.palette.mode === 'dark'
          ? '1px solid rgba(255, 255, 255, 0.08)'
          : '1px solid rgba(0, 0, 0, 0.08)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        px: 2,
        zIndex: theme.zIndex.drawer + 2,
      }}
    >
      {/* Left section - Status indicators and Branding */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        {/* Spekta Branding */}
        <Tooltip title="Powered by Spekta - Premium Software Solutions">
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              px: 1,
              py: 0.25,
              borderRadius: '6px',
              background: theme.palette.mode === 'dark'
                ? 'linear-gradient(135deg, rgba(25, 118, 210, 0.15) 0%, rgba(156, 39, 176, 0.15) 100%)'
                : 'linear-gradient(135deg, rgba(25, 118, 210, 0.1) 0%, rgba(156, 39, 176, 0.1) 100%)',
              border: theme.palette.mode === 'dark'
                ? '1px solid rgba(25, 118, 210, 0.3)'
                : '1px solid rgba(25, 118, 210, 0.2)',
              cursor: 'default',
              transition: 'all 0.2s',
              '&:hover': {
                background: theme.palette.mode === 'dark'
                  ? 'linear-gradient(135deg, rgba(25, 118, 210, 0.25) 0%, rgba(156, 39, 176, 0.25) 100%)'
                  : 'linear-gradient(135deg, rgba(25, 118, 210, 0.15) 0%, rgba(156, 39, 176, 0.15) 100%)',
              }
            }}
          >
            <SpektaIcon 
              sx={{ 
                fontSize: '0.85rem',
                color: theme.palette.primary.main,
              }} 
            />
            <Typography
              variant="caption"
              sx={{
                fontSize: '0.7rem',
                fontWeight: 600,
                background: theme.palette.mode === 'dark'
                  ? 'linear-gradient(135deg, #1976d2 0%, #9c27b0 100%)'
                  : 'linear-gradient(135deg, #1565c0 0%, #7b1fa2 100%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                letterSpacing: '0.5px',
              }}
            >
              Powered by Spekta
            </Typography>
          </Box>
        </Tooltip>

        {/* Online/Offline Status */}
        <Tooltip title={isOnline ? 'Connected' : 'Offline'}>
          <Chip
            icon={isOnline ? <OnlineIcon /> : <OfflineIcon />}
            label={isOnline ? 'Online' : 'Offline'}
            size="small"
            color={isOnline ? 'success' : 'error'}
            sx={{
              height: '24px',
              fontSize: '0.75rem',
              fontWeight: 500,
              '& .MuiChip-icon': {
                fontSize: '0.9rem',
              },
            }}
          />
        </Tooltip>

        {/* Update Status */}
        {updateStatus && (
          <Tooltip title={updateStatus}>
            <Typography
              variant="caption"
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                color: theme.palette.text.secondary,
                fontSize: '0.75rem',
              }}
            >
              <UpdateIcon sx={{ fontSize: '0.9rem' }} />
              {updateStatus.length > 30 ? `${updateStatus.substring(0, 30)}...` : updateStatus}
            </Typography>
          </Tooltip>
        )}

        {/* App Version */}
        {appVersion && (
          <Tooltip title="Application Version">
            <Typography
              variant="caption"
              sx={{
                color: theme.palette.text.secondary,
                fontSize: '0.75rem',
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
              }}
            >
              <InfoIcon sx={{ fontSize: '0.9rem' }} />
              v{appVersion}
            </Typography>
          </Tooltip>
        )}
      </Box>

      {/* Right section - Zoom controls, Date and Time */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        {/* Zoom controls (only in Electron) */}
        {isElectron && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              px: 1,
              borderRadius: '8px',
              backgroundColor: zoomPercentage !== 100
                ? (theme.palette.mode === 'dark'
                  ? 'rgba(25, 118, 210, 0.15)'
                  : 'rgba(25, 118, 210, 0.1)')
                : (theme.palette.mode === 'dark'
                  ? 'rgba(255, 255, 255, 0.05)'
                  : 'rgba(0, 0, 0, 0.03)'),
              border: zoomPercentage !== 100
                ? `1px solid ${theme.palette.primary.main}`
                : (theme.palette.mode === 'dark'
                  ? '1px solid rgba(255, 255, 255, 0.08)'
                  : '1px solid rgba(0, 0, 0, 0.08)'),
              transition: 'all 0.2s',
            }}
          >
            <Tooltip title="Zoom Out (Ctrl/Cmd + -)">
              <IconButton
                size="small"
                onClick={handleZoomOut}
                sx={{ 
                  padding: '2px',
                  color: zoomPercentage !== 100 ? theme.palette.primary.main : 'inherit',
                  '&:hover': {
                    backgroundColor: theme.palette.mode === 'dark'
                      ? 'rgba(255, 255, 255, 0.1)'
                      : 'rgba(0, 0, 0, 0.05)',
                  }
                }}
              >
                <ZoomOut sx={{ fontSize: '0.9rem' }} />
              </IconButton>
            </Tooltip>

            <Tooltip title={zoomPercentage === 100 ? "Default zoom" : "Click to reset to 100% (Ctrl/Cmd + 0)"}>
              <Box
                onClick={handleZoomReset}
                sx={{
                  minWidth: '45px',
                  textAlign: 'center',
                  cursor: zoomPercentage !== 100 ? 'pointer' : 'default',
                  padding: '2px 6px',
                  borderRadius: '4px',
                  backgroundColor: zoomPercentage !== 100
                    ? (theme.palette.mode === 'dark'
                      ? 'rgba(25, 118, 210, 0.2)'
                      : 'rgba(25, 118, 210, 0.15)')
                    : 'transparent',
                  '&:hover': zoomPercentage !== 100 ? {
                    backgroundColor: theme.palette.mode === 'dark'
                      ? 'rgba(25, 118, 210, 0.3)'
                      : 'rgba(25, 118, 210, 0.2)',
                  } : {},
                  transition: 'background-color 0.2s',
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    fontSize: '0.72rem',
                    fontWeight: 700,
                    fontFamily: 'monospace',
                    color: zoomPercentage !== 100 
                      ? theme.palette.primary.main
                      : theme.palette.text.secondary,
                  }}
                >
                  {zoomPercentage}%
                </Typography>
              </Box>
            </Tooltip>

            <Tooltip title="Zoom In (Ctrl/Cmd + +)">
              <IconButton
                size="small"
                onClick={handleZoomIn}
                sx={{ 
                  padding: '2px',
                  color: zoomPercentage !== 100 ? theme.palette.primary.main : 'inherit',
                  '&:hover': {
                    backgroundColor: theme.palette.mode === 'dark'
                      ? 'rgba(255, 255, 255, 0.1)'
                      : 'rgba(0, 0, 0, 0.05)',
                  }
                }}
              >
                <ZoomIn sx={{ fontSize: '0.9rem' }} />
              </IconButton>
            </Tooltip>
          </Box>
        )}

        <Typography
          variant="caption"
          sx={{
            color: theme.palette.text.secondary,
            fontSize: '0.75rem',
            fontWeight: 500,
          }}
        >
          {formatDate(currentTime)}
        </Typography>
        <Typography
          variant="caption"
          sx={{
            color: theme.palette.primary.main,
            fontSize: '0.75rem',
            fontWeight: 600,
            fontFamily: 'monospace',
          }}
        >
          {formatTime(currentTime)}
        </Typography>
      </Box>
    </Box>
  );
};

export default StatusBar;